import React from 'react';
import { Box, Container, Card, CardContent, Typography, Grid, InputBase, alpha, styled } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { TopBarContext } from '../TopBar';
import { useSearch } from '../hooks/useSearch';
import { ShowItem } from '../classes/showItem';
import ShowItems from '../components/ShowItems';
import Progress from '../components/Progress';
import { ErrorDialogContext } from '../components/ErrorDialog';

let lastSearch = 0;

const SearchInput = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 1),
  border: '1px solid #e0e0e0',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  marginBottom: theme.spacing(4),
  width: '100%',
  maxWidth: 600,
  margin: '0 auto',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(2, 2, 2, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: '100%',
  },
}));

const NavigationCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  borderRadius: 16,
  height: '100%',
  minHeight: 160,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
    borderColor: theme.palette.primary.main,
  },
}));

interface NavCardProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
}

const NavCard: React.FC<NavCardProps> = ({ title, icon, color, onClick }) => (
  <Grid item xs={6} sm={6} md={3}>
    <NavigationCard onClick={onClick} elevation={2}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Box sx={{ 
          width: 80, 
          height: 80, 
          margin: '0 auto 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': {
            width: 64,
            height: 64,
            fill: color,
          }
        }}>
          {icon}
        </Box>
        <Typography variant="h6" fontWeight="600" color="text.primary">
          {title}
        </Typography>
      </CardContent>
    </NavigationCard>
  </Grid>
);

function Home() {
  const navigate = useNavigate();
  const topBarCtx = React.useContext(TopBarContext);
  const errorDialogCtx = React.useContext(ErrorDialogContext);
  const [search, setSearch] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<ShowItem[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [handleSearch] = useSearch();

  React.useEffect(() => {
    topBarCtx?.setTitle('Home');
  }, []);

  React.useEffect(() => {
    setLoading(true);
    handleSearch(search).then((res) => {
      setResults(res);
      setLoading(false);
    }).catch((err) => {
      errorDialogCtx?.setContent(err.message);
      errorDialogCtx?.setOpen(true);
      setLoading(false);
    });

    const n = lastSearch + 1;
    lastSearch = n;
    setTimeout(() => {
      if (n !== lastSearch) {
        return;
      }

      if (search) {
        if (searchParams.has('q') && searchParams.get('q') === search) {
          return;
        }
        setSearchParams({ q: search });
      } else if (searchParams.has('q')) {
        setSearchParams({});
      }
    }, 1000);
  }, [search]);

  React.useEffect(() => {
    if (searchParams.has('q')) {
      setSearch(searchParams.get('q') as string);
    } else {
      setSearch(null);
    }
  }, [searchParams]);


  const navigationCards = [
    {
      title: 'Patologie',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
          <path d="M450-80q-12 0-21-9t-9-21q0-12 9-21t21-9v-62q-42-5-79-20t-67-40l-43 44q9 9 9 21t-9 21q-9 9-21 9t-21-9l-43-42q-9-9-9-21.5t9-21.5q9-9 21-8.5t21 8.5l44-44q-25-31-40-67t-20-78h-62q0 12-9 21t-21 9q-12 0-21-9t-9-21v-60q0-12 9-21t21-9q12 0 21 9t9 21h62q5-42 20.5-78t39.5-67l-44-44q-9 8-21 8.5t-21-8.5q-9-9-9-21.5t9-21.5l42-42q9-9 21.5-9t21.5 9q9 9 9 21.5t-9 21.5l43 43q31-25 67-40t78-20v-62q-12 0-20.5-9t-8.5-21q0-12 9-21t21-9h60q12 0 21 9t9 21q0 12-9 21t-21 9v62q42 5 78 20t67 40l44-44q-9-9-9-21t9-21q9-9 21.5-9t21.5 9l42 43q9 9 9 21t-9 21q-9 9-21.5 9t-21.5-9l-43 43q25 31 40 67.5t20 78.5h62q0-12 9-21t21-9q12 0 21 9t9 21v60q0 12-9 21t-21 9q-12 0-21-9t-9-21h-62q-5 42-20 78.5T698-304l43 43q9-9 21.5-9t21.5 9q9 9 9 21.5t-9 21.5l-42 42q-9 9-21.5 9t-21.5-9q-9-9-8.5-21t8.5-21l-44-44q-31 25-67 40.5T510-201v61q12 0 21 9t9 21q0 12-9 21t-21 9h-60Zm30-200q83 0 141.5-58.5T680-480q0-83-58.5-141.5T480-680q-83 0-141.5 58.5T280-480q0 83 58.5 141.5T480-280Zm-41.5-51.5Q450-343 450-360t-11.5-28.5Q427-400 410-400t-28.5 11.5Q370-377 370-360t11.5 28.5Q393-320 410-320t28.5-11.5ZM550-320q17 0 28.5-11.5T590-360q0-17-11.5-28.5T550-400q-17 0-28.5 11.5T510-360q0 17 11.5 28.5T550-320ZM340-440q17 0 28.5-11.5T380-480q0-17-11.5-28.5T340-520q-17 0-28.5 11.5T300-480q0 17 11.5 28.5T340-440Zm168.5-11.5Q520-463 520-480t-11.5-28.5Q497-520 480-520t-28.5 11.5Q440-497 440-480t11.5 28.5Q463-440 480-440t28.5-11.5ZM620-440q17 0 28.5-11.5T660-480q0-17-11.5-28.5T620-520q-17 0-28.5 11.5T580-480q0 17 11.5 28.5T620-440ZM438.5-571.5Q450-583 450-600t-11.5-28.5Q427-640 410-640t-28.5 11.5Q370-617 370-600t11.5 28.5Q393-560 410-560t28.5-11.5ZM550-560q17 0 28.5-11.5T590-600q0-17-11.5-28.5T550-640q-17 0-28.5 11.5T510-600q0 17 11.5 28.5T550-560Zm-70 80Z"/>
        </svg>
      ),
      color: '#E53935',
      onClick: () => navigate('/list/pathologies'),
    },
    {
      title: 'Farmaci',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
          <path d="M120-120v-80l80-240-80-240v-80h508l58-160 94 34-46 126h106v80l-80 240 80 240v80H120Zm320-160h80v-120h120v-80H520v-120h-80v120H320v80h120v120Zm-236 80h552l-80-240 80-240H204l80 240-80 240Zm276-240Z"/>
        </svg>
      ),
      color: '#43A047',
      onClick: () => navigate('/list/drugs'),
    },
    {
      title: 'Report',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
          <path d="M595-355q-35-35-35-85t35-85q35-35 85-35t85 35q35 35 35 85t-35 85q-35 35-85 35t-85-35Zm113.5-56.5Q720-423 720-440t-11.5-28.5Q697-480 680-480t-28.5 11.5Q640-457 640-440t11.5 28.5Q663-400 680-400t28.5-11.5ZM440-40v-116q0-21 10-39.5t28-29.5q32-19 67.5-31.5T618-275l62 75 62-75q37 6 72 18.5t67 31.5q18 11 28.5 29.5T920-156v116H440Zm79-80h123l-54-66q-18 5-35 13t-34 17v36Zm199 0h122v-36q-16-10-33-17.5T772-186l-54 66Zm-76 0Zm76 0Zm-518 0q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v200q-16-20-35-38t-45-24v-138H200v560h166q-3 11-4.5 22t-1.5 22v36H200Zm80-480h280q26-20 57-30t63-10v-40H280v80Zm0 160h200q0-21 4.5-41t12.5-39H280v80Zm0 160h138q11-9 23.5-16t25.5-13v-51H280v80Zm-80 80v-560 137-17 440Zm480-240Z"/>
        </svg>
      ),
      color: '#FDD835',
      onClick: () => navigate('/report'),
    },
    {
      title: 'Informazioni',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960">
          <path d="M440-280h80v-240h-80v240Zm68.5-331.5Q520-623 520-640t-11.5-28.5Q497-680 480-680t-28.5 11.5Q440-657 440-640t11.5 28.5Q463-600 480-600t28.5-11.5ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
        </svg>
      ),
      color: '#1E88E5',
      onClick: () => navigate('/info'),
    },
  ];

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
    }}>
      <Container maxWidth="md" sx={{ flex: 1, py: 4 }}>
        {/* Logo */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: 1, 
          mb: 2, 
          mt: 1 
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 2 
          }}>
            <img 
              src="/logo.svg" 
              alt="DOCPLUS" 
              style={{ 
                width: 120, 
                height: 120,
                objectFit: 'contain'
              }} 
            />
            <img 
              src="/logo_unito.svg" 
              alt="UNITO" 
              style={{ 
                width: 120, 
                height: 120,
                objectFit: 'contain'
              }} 
            />
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 2 
          }}>
            <img 
              src="/logo_regione.svg" 
              alt="Regione Piemonte" 
              style={{ 
                width: 120, 
                height: 120,
                objectFit: 'contain'
              }} 
            />
            <img 
              src="/logo_simg.webp" 
              alt="SIMG" 
              style={{ 
                width: 120, 
                height: 120,
                objectFit: 'contain'
              }} 
            />
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 6 }}>
          <SearchInput>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Cerca..."
              inputProps={{ 'aria-label': 'search', 'value': search ?? '' }}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchInput>
        </Box>

        {/* Search Results or Navigation Cards */}
        {search && search.trim() ? (
          <Box>
            {loading && <Progress />}
            <ShowItems items={results} urlPrefix='/list' showScrollToTop />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {navigationCards.map((card) => (
              <NavCard
                key={card.title}
                title={card.title}
                icon={card.icon}
                color={card.color}
                onClick={card.onClick}
              />
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

export default Home;
